import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Fragment } from 'react/jsx-runtime';

import {
  DEFAULT_MANUAL_UPLOAD_ACCEPTED_FORMATS,
  DEFAULT_MANUAL_UPLOAD_MAX_SIZE,
} from '@/constants';
import { parseFileFormats } from '@/helpers';
import { useCustomSearchParams } from '@/hooks';
import { getFileNameFromPath, mergeClasses } from '@/utils';

import {
  AlertTriangleIcon,
  CheckCircleBrokenIcon,
  CheckCircleIcon,
  DocumentErrorIcon,
  FolderInjectIcon,
  Trash01Icon,
  Upload04Icon,
} from '@/assets/icons';
import {
  Badge,
  BaseModal,
  Button,
  ConfirmationDialog,
  FileUpload,
  ItemContent,
  Label,
  Loading,
  Switch,
} from '@/components/common';

import { LinkStatus } from '@/features/regulatory-intake/enums';

import {
  useAttentionDocuments,
  useResolveWithDocumentMutation,
  useResolveWithoutDocumentMutation,
  useRevertDocumentMutation,
  useTransferToN1Mutation,
} from '@/features/regulatory-intake/intake-details/hooks';
import {
  getLinkStatusIcon,
  getLinkStatusLabel,
  getLinkStatusVariant,
} from '@/features/regulatory-intake/intake-details/utils';

interface IManualUploadModal {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRefetch: () => void;
  emailId: string;
}

type ConfirmationType =
  | 'documentResolved'
  | 'documentReverted'
  | 'noDocumentFlagResolved'
  | 'noDocumentFlagReverted'
  | 'transfer'
  | null;

const ManualUploadModal = ({ open, onOpenChange, emailId, onRefetch }: IManualUploadModal) => {
  const {
    data: documents,
    refetch: refetchDocuments,
    isFetching,
    isLoading,
  } = useAttentionDocuments(emailId);

  const { mutateAsync: resolveWithDocument, isPending: isResolvingWithDocument } =
    useResolveWithDocumentMutation();
  const { mutateAsync: resolveWithoutDocument, isPending: isResolvingWithoutDocument } =
    useResolveWithoutDocumentMutation();
  const { mutateAsync: revertDocument, isPending: isRevertingDocument } =
    useRevertDocumentMutation();
  const { mutateAsync: transferToN1, isPending: isTransferringToN1 } = useTransferToN1Mutation();

  const [searchParams, setSearchParams] = useCustomSearchParams();

  const [selectedDocumentId, setSelectedDocumentId] = useState<string>(
    () => searchParams.get('selectedDocumentId') || '',
  );
  const [hasDocumentsUpdated, setHasDocumentsUpdated] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [confirmationType, setConfirmationType] = useState<ConfirmationType>(null);

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const selectedDocument = useMemo(() => {
    return documents?.find((document) => document.id === selectedDocumentId);
  }, [documents, selectedDocumentId]);

  const [error, setError] = useState('');

  const resolvedDocuments = useMemo(() => {
    return documents?.filter((document) => document.status === LinkStatus.Resolved);
  }, [documents]);
  const isAllDocResolved = useMemo(() => {
    return documents?.every((document) => document.status === LinkStatus.Resolved);
  }, [documents]);

  const showResolvedDocumentBeforeUpload = useMemo(() => {
    return (
      selectedDocument &&
      selectedDocument.status === LinkStatus.Resolved &&
      !selectedDocument.documentPath &&
      !selectedDocument.noDocumentFlag
    );
  }, [selectedDocument]);

  const disabledNoDocumentSwitch = useMemo(() => {
    return (
      selectedDocument &&
      selectedDocument.status === LinkStatus.Resolved &&
      !!selectedDocument.documentPath
    );
  }, [selectedDocument]);

  const showUploader = useMemo(() => {
    return (
      selectedDocument &&
      (selectedDocument.status === LinkStatus.Attention ||
        (selectedDocument.status === LinkStatus.Resolved &&
          !!selectedDocument.documentPath &&
          !selectedDocument.noDocumentFlag))
    );
  }, [selectedDocument]);

  const ACCEPTED_FORMATS = parseFileFormats(
    process.env.REACT_APP_MANUAL_UPLOAD_ACCEPTED_FORMATS || DEFAULT_MANUAL_UPLOAD_ACCEPTED_FORMATS,
  );

  const MAX_SIZE = parseInt(
    process.env.REACT_APP_MANUAL_UPLOAD_MAX_SIZE || DEFAULT_MANUAL_UPLOAD_MAX_SIZE.toString(),
  );

  const onOpen = (value: boolean) => {
    if (!value) {
      setError('');
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('selectedDocumentId');
      setSearchParams(newParams, { replace: true });
      setSelectedDocumentId('');
    }

    if (!value && hasDocumentsUpdated) {
      onRefetch();
    }

    onOpenChange(value);
  };

  const onDropFile = async (files: File[]) => {
    if (files[0]) {
      setConfirmationType('documentResolved');
      setUploadedFile(files[0]);
      setShowConfirmationDialog(true);
    }
  };

  const onToggleNoDocumentFlag = async (isNoDocumentFlagResolved: boolean) => {
    if (isNoDocumentFlagResolved) {
      setConfirmationType('noDocumentFlagResolved');
    } else {
      setConfirmationType('noDocumentFlagReverted');
    }

    setShowConfirmationDialog(true);
  };

  const onResolveWithDocument = async () => {
    await resolveWithDocument(
      {
        emailId: emailId,
        documentId: selectedDocument?.id?.toString() || '',
        file: uploadedFile!,
      },
      {
        onSuccess: () => {
          refetchDocuments();
          setError('');
          setHasDocumentsUpdated(true);
        },
        onSettled: () => {
          setShowConfirmationDialog(false);
        },
      },
    );
  };

  const onResolveWithoutDocument = async () => {
    await resolveWithoutDocument(
      {
        emailId: emailId,
        documentId: selectedDocument?.id || '',
      },
      {
        onSuccess: () => {
          refetchDocuments();
          setHasDocumentsUpdated(true);
        },
        onSettled: () => {
          setShowConfirmationDialog(false);
        },
      },
    );
  };

  const onRevertDocument = async () => {
    await revertDocument(
      {
        emailId: emailId,
        documentId: selectedDocument?.id || '',
      },
      {
        onSuccess: () => {
          refetchDocuments();
          setHasDocumentsUpdated(true);
        },
        onSettled: () => {
          setShowConfirmationDialog(false);
        },
      },
    );
  };

  const onTransferToN1 = async () => {
    await transferToN1(emailId, {
      onSuccess: () => {
        onOpenChange(false);
        onRefetch();
      },
      onSettled: () => {
        setShowConfirmationDialog(false);
      },
    });
  };

  const getConfirmationDialog = () => {
    if (confirmationType === 'documentResolved') {
      return (
        <ConfirmationDialog
          open
          onOpenChange={setShowConfirmationDialog}
          closeOnEscape={false}
          icon={CheckCircleBrokenIcon}
          variant="green"
          title="Resolved Link"
          subtitle="Uploading this document will switch the status of the link to Resolved."
          onConfirm={onResolveWithDocument}
          isConfirming={isResolvingWithDocument}
        />
      );
    } else if (confirmationType === 'documentReverted') {
      return (
        <ConfirmationDialog
          open
          onOpenChange={setShowConfirmationDialog}
          closeOnEscape={false}
          variant="red"
          icon={AlertTriangleIcon}
          title="Link will be change to Attention"
          subtitle="Deleting this document will switch the status of the link to Attention."
          onConfirm={onRevertDocument}
          isConfirming={isRevertingDocument}
        />
      );
    } else if (confirmationType === 'noDocumentFlagResolved') {
      return (
        <ConfirmationDialog
          open
          onOpenChange={setShowConfirmationDialog}
          closeOnEscape={false}
          icon={CheckCircleBrokenIcon}
          variant="green"
          title="Resolved Link"
          subtitle="Turning this toggle on will switch the status of the link to Resolved."
          onConfirm={onResolveWithoutDocument}
          isConfirming={isResolvingWithoutDocument}
        />
      );
    } else if (confirmationType === 'noDocumentFlagReverted') {
      return (
        <ConfirmationDialog
          open
          onOpenChange={setShowConfirmationDialog}
          closeOnEscape={false}
          variant="red"
          icon={AlertTriangleIcon}
          title="Link will be change to Attention"
          subtitle="Turning this toggle off will switch the status of the link to Attention."
          onConfirm={onRevertDocument}
          isConfirming={isRevertingDocument}
        />
      );
    }

    return (
      <ConfirmationDialog
        open
        onOpenChange={setShowConfirmationDialog}
        icon={Upload04Icon}
        variant="green"
        title="Transfer to N1"
        subtitle="Please confirm that all required documents have been uploaded. Once confirmed, the message will be transferred to N1 for downstream processing."
        onConfirm={onTransferToN1}
        isConfirming={isTransferringToN1}
      />
    );
  };

  // After each refetch, keep the current selection if it is still in the list.
  // Otherwise fall back to the first document (and the URL sync effect below
  // will update the query param accordingly).
  useEffect(() => {
    if (!documents || documents.length === 0) return;
    if (documents.some((document) => document.id === selectedDocumentId)) return;
    setSelectedDocumentId(documents[0].id);
  }, [documents, selectedDocumentId]);

  // Sync the selected document id to the URL so the selection is preserved
  // across refetches, page refreshes, and is shareable via URL.
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (selectedDocumentId) {
      newParams.set('selectedDocumentId', selectedDocumentId);
    } else {
      newParams.delete('selectedDocumentId');
    }
    if (newParams.toString() !== searchParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
  }, [selectedDocumentId, searchParams, setSearchParams]);

  const renderContent = () => {
    return (
      <>
        {documents && documents.length > 0 ? (
          <>
            <div className="flex flex-row border border-neutral-4 rounded-xs overflow-hidden !w-full max-h-[432px] min-h-[324px]">
              <div className="flex flex-col border-r border-neutral-4 w-fit overflow-x-hidden overflow-y-auto">
                {documents.map((document, index) => (
                  <Fragment key={document.id}>
                    {index >= 1 && <div className="border-b border-neutral-4"></div>}
                    <ItemContent
                      isSelected={selectedDocument?.id === document.id}
                      className="!p-4 w-[504px]"
                      onClick={() => {
                        setSelectedDocumentId(document.id);
                      }}
                    >
                      <div className="flex flex-col gap-2 justify-between items-start !w-[432px]">
                        <Badge
                          variant={getLinkStatusVariant(document.status)}
                          size="sm"
                          mode="light"
                          borderRadius="rounded"
                          border
                        >
                          {getLinkStatusIcon(document.status, '!size-3 mr-1')}
                          {getLinkStatusLabel(document.status)}
                        </Badge>
                        <Link
                          to={document.url}
                          target="_blank"
                          className="underline break-all line-clamp-2 text-left w-[390px] font-medium text-body-sm text-primary-base"
                          title={document.url}
                        >
                          {document.url}
                        </Link>
                      </div>
                    </ItemContent>
                  </Fragment>
                ))}
              </div>
              <div className="flex-1 p-6 bg-neutral-1 gap-4">
                <div
                  className={mergeClasses(
                    'flex flex-row justify-between items-center w-full',
                    !showResolvedDocumentBeforeUpload && 'justify-start',
                  )}
                >
                  <span className="font-bold text-h6 text-neutral-9">Uploaded Document</span>
                  {!showResolvedDocumentBeforeUpload && (
                    <div className="flex flex-row justify-between items-center gap-2">
                      <span
                        className={mergeClasses(
                          'font-semibold text-body-sm text-neutral-9',
                          disabledNoDocumentSwitch && '!text-neutral-7',
                        )}
                      >
                        No document in link
                      </span>
                      <Switch
                        size="sm"
                        checked={!!selectedDocument?.noDocumentFlag}
                        onCheckedChange={(checked) => onToggleNoDocumentFlag(checked)}
                        disabled={disabledNoDocumentSwitch}
                      />
                    </div>
                  )}
                </div>
                {showUploader && (
                  <div className="mt-4">
                    <div className="mb-2">
                      <Label className="!gap-0 font-semibold !text-body-base text-neutral-9">
                        Document <span className="ml-1 text-semantic-error-base">*</span>
                      </Label>
                    </div>
                    {selectedDocument?.documentPath ? (
                      <>
                        <div className="flex flex-row justify-between items-center gap-4">
                          <div className="flex w-full items-center justify-between gap-2 h-12 px-4 py-3 border border-neutral-4 rounded-small bg-neutral-0 max-w-[584px]">
                            <div className="flex items-center gap-2 min-w-0">
                              <CheckCircleIcon className="size-6 shrink-0 text-semantic-success-base" />
                              <span className="truncate text-base font-medium text-neutral-9 dark:text-neutral-1">
                                {getFileNameFromPath(selectedDocument.documentPath)}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            aria-label="Remove uploaded file"
                            className="flex shrink-0 items-center justify-center"
                            onClick={() => {
                              setConfirmationType('documentReverted');
                              setShowConfirmationDialog(true);
                            }}
                          >
                            <Trash01Icon className="size-5 text-neutral-7" />
                          </button>
                        </div>
                        <p className="mt-2 text-wrap text-body-sm font-medium text-neutral-7">
                          You can only upload .pdf files. Files can’t be larger than 50MB.
                        </p>
                      </>
                    ) : (
                      <FileUpload
                        variant="compact"
                        error={error}
                        onDrop={onDropFile}
                        onError={setError}
                        accept={ACCEPTED_FORMATS}
                        maxSize={MAX_SIZE}
                        disabled={showConfirmationDialog}
                      />
                    )}
                  </div>
                )}
                {showResolvedDocumentBeforeUpload && (
                  <div className="flex flex-col w-full h-full justify-center items-center gap-4">
                    <FolderInjectIcon />
                    <div className="font-medium text-body-sm text-neutral-7 w-[388px] text-center">
                      This extracted link is already resolved and the document associated with this
                      link has been saved.
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-6 flex flex-row justify-between items-center">
              <Badge variant="teal" size="md" borderRadius="rounded" className="px-6">
                Resolved Links: {resolvedDocuments?.length}/{documents?.length}
              </Badge>
              <div className="flex flex-row justify-between items-center gap-3">
                <Button
                  buttonStyle="outline"
                  buttonColor="neutral"
                  size="lg"
                  borderRadius="soft"
                  className="w-[104px]"
                  onClick={() => onOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  buttonStyle="filled"
                  buttonColor="primary"
                  size="lg"
                  borderRadius="soft"
                  className="w-[104px]"
                  disabled={!isAllDocResolved}
                  onClick={() => {
                    setConfirmationType('transfer');
                    setShowConfirmationDialog(true);
                  }}
                >
                  Transfer
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col w-full justify-center items-center gap-4">
            <DocumentErrorIcon />
            <span className="font-medium text-body-sm text-neutral-7">
              The document links could not be loaded.
              <br />
              Please refresh the page and try again.
            </span>
          </div>
        )}
        {showConfirmationDialog && getConfirmationDialog()}
      </>
    );
  };

  return (
    <BaseModal
      hideFooter
      open={open}
      onOpen={onOpen}
      title="Manual Upload"
      contentClassName="min-w-[1220px] max-w-[1220px] min-h-[496px] max-h-[604px]"
      closeOnEscape={false}
    >
      {isLoading || isFetching ? (
        <div className="relative h-[396px] w-full">
          <Loading className="w-full h-full" />
        </div>
      ) : (
        renderContent()
      )}
    </BaseModal>
  );
};

export default ManualUploadModal;
